import React, { useEffect, useState } from 'react'
import { DropDownList } from '@progress/kendo-react-dropdowns'
import { TeamResourcesSalesData } from '../../../features/sales/salesAPI'
import styles from '../../TechPartnerDashboard/TechPartnerDashboard.module.scss'
import '../../TechPartnerDashboard/TechPartnerDashboard.scss'
import {
    Grid,
    GridColumn,
    GridColumnMenuCheckboxFilter,
    GridSortChangeEvent,
} from '@progress/kendo-react-grid'
import { CompositeFilterDescriptor, orderBy, SortDescriptor } from '@progress/kendo-data-query'

const TECHNICAL_KNOWLEDGE_LEVELS: GridLevel[] = [
    {
        levelId: 1,
        title: 'Novice - limited technical (Can provide basic info on product)',
        levelText: 'Novice - limited technical',
    },
    {
        levelId: 3,
        title: 'Basic knowledge of core technologies (Can select a product and knows who to go to for the information)',
        levelText: 'Basic knowledge of core technologies',
    },
    {
        levelId: 5,
        title: 'Expertise at one core technology (Can help others on a core technologies/products)',
        levelText: 'Expertise at one core technology',
    },
    {
        levelId: 7,
        title: 'Expertise on multiple technologies (Can help others with multiple technologies/products)',
        levelText: 'Expertise on multiple technologies',
    },
    {
        levelId: 9,
        title: 'System Solutions Expert (Can help others to build complete systems to solve solution)',
        levelText: 'System Solutions Expert',
    },
]

const SALES_KNOWLEDGE_LEVELS: GridLevel[] = [
    {
        levelId: 1,
        title: 'Novice - transactional selling (Can work with others to provide pricing and lead time to customers)',
        levelText: 'Novice - transactional selling',
    },
    {
        levelId: 3,
        title: 'Transactional Selling (Can provide pricing and lead time to customers)',
        levelText: 'Transactional Selling',
    },
    {
        levelId: 5,
        title: 'Product Selling - promote products (Can help select and identify best fit and alternatives)',
        levelText: 'Product Selling',
    },
    {
        levelId: 7,
        title: 'Technical Consultant - promote projects (Can select and design solutions to meet customer needs)',
        levelText: 'Technical Consultant',
    },
    {
        levelId: 9,
        title: 'Business Consultant - calls on Sr Mgs - promotes AWC Value (Can help identify customer ‘core’ needs and build solutions to help customer meet their company initiatives)',
        levelText: 'Business Consultant',
    },
]

interface TeamResourcesGridProps {
    data: Array<TeamResourcesSalesData>
}

interface GridLevel {
    levelId: number
    title: string
    levelText: string
}

export const TeamResourcesGrid = (props: TeamResourcesGridProps) => {
    const initialSort: SortDescriptor[] = []
    const [sort, setSort] = React.useState(initialSort)

    const [fullDepartmentList, setFullDepartmentList] = useState<string[]>()

    const [departmentFilter, setDepartmentFilter] = useState<string[]>([''])
    const [fullDepartmentFilter, setFullDepartmentFilter] =
        useState<CompositeFilterDescriptor | null>(null)

    const [gridData, setGridData] = useState<TeamResourcesSalesData[]>([])

    const GridDropDownList = (
        data: GridLevel[],
        value: number | null,
        onChange: (newValue: any) => void
    ): JSX.Element => {
        return (
            <DropDownList
                data={data}
                value={value}
                textField='levelText'
                dataItemKey='levelId'
                onChange={(e: any) => {
                    e.target.element.title = e.value.title
                    onChange(e.value)
                }}
                itemRender={(li: React.ReactElement<HTMLLIElement>) => {
                    const levelTitle = data.find(
                        (l) => l.levelText === li.props.children[0].toString()
                    )?.title
                    return React.cloneElement(
                        li,
                        { ...li.props, title: levelTitle },
                        li.props.children
                    )
                }}
            />
        )
    }

    function getTeamResourcesDataPayload() {
        const filteredResources = gridData.filter(
            (r) =>
                departmentFilter.length === 0 ||
                (departmentFilter.length === 1 && departmentFilter[0].length === 0) ||
                departmentFilter.indexOf(r.Department) > -1
        )
        const data = filteredResources.concat(
            new Array(filteredResources.length > 0 ? 0 : 5).fill({
                Name: '',
                Department: '',
                Position: '',
                YTDPlan: 0,
                YTDSales: 0,
                PercentOfTotal: 0,
                TechnicalKnowledge: null,
                SalesKnowledge: null,
            })
        )
        return data
    }

    const DepartmentCheckboxFilter = (props: any) => {
        return (
            <div>
                <GridColumnMenuCheckboxFilter
                    {...props}
                    data={
                        fullDepartmentList &&
                        fullDepartmentList.map((str: string) => {
                            return { Department: str }
                        })
                    }
                    filter={fullDepartmentFilter}
                    expanded={true}
                    onFilterChange={(filter: CompositeFilterDescriptor | null) => {
                        setFullDepartmentFilter(filter)
                    }}
                />
            </div>
        )
    }

    const setCustomSort = (curSort: SortDescriptor[]) => {
        const sortByAscendingFields = ['Name', 'Department', 'Position']

        curSort = curSort.map((s) => {
            let newDirection = 'asc'
            if (sortByAscendingFields.indexOf(s.field) === -1 && s.dir === 'asc')
                newDirection = 'desc'
            return { ...s, dir: newDirection } as SortDescriptor
        })

        return curSort
    }

    useEffect(() => {
        if (props.data) setFullDepartmentList(props.data.map((c) => c.Department!))
        setGridData(props.data)
    }, [props.data])

    useEffect(() => {
        const tempFilters: string[] = []

        if (fullDepartmentFilter && fullDepartmentFilter.filters.length > 0) {
            fullDepartmentFilter.filters.forEach((filter: any) => {
                filter.filters.forEach((f: any) => {
                    tempFilters.push(f.value)
                })
            })
            setDepartmentFilter(tempFilters)
        } else {
            setDepartmentFilter([''])
        }
    }, [fullDepartmentFilter])

    return (
        <Grid
            data={orderBy(getTeamResourcesDataPayload(), sort)}
            style={{
                width: '100%',
            }}
            filterable={false}
            sortable={true}
            sort={sort}
            onSortChange={(e: GridSortChangeEvent) => {
                setSort(setCustomSort(e.sort))
            }}
            scrollable={'none'}
        >
            <GridColumn
                title='Name'
                field='Name'
                cell={(props: any) => (
                    <td className={`${styles.dataLabel} ${styles.alignLeft}`}>
                        {props.dataItem.Name ? props.dataItem.Name : ''}
                    </td>
                )}
            />
            <GridColumn
                title='Department'
                field='Department'
                cell={(props: any) => (
                    <td className={`${styles.dataLabel} ${styles.alignLeft}`}>
                        {props.dataItem.Name ? props.dataItem.Department : ''}
                    </td>
                )}
                columnMenu={DepartmentCheckboxFilter}
            />
            <GridColumn
                title='Position'
                field='Position'
                cell={(props: any) => (
                    <td className={`${styles.dataLabel} ${styles.alignLeft}`}>
                        {props.dataItem.Name ? props.dataItem.Position : ''}
                    </td>
                )}
            />
            <GridColumn
                title='Plan YTD'
                field='YTDPlan'
                headerClassName='dataCurrency'
                cell={(props: any) => (
                    <td className={styles.dataCurrency}>
                        {props.dataItem.Name
                            ? props.dataItem.YTDPlan?.toLocaleString('en-US', {
                                  style: 'currency',
                                  currency: 'USD',
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 0,
                              })
                            : ''}
                    </td>
                )}
            />
            <GridColumn
                title='Sales YTD'
                field='YTDSales'
                headerClassName='dataCurrency'
                cell={(props: any) => (
                    <td className={styles.dataCurrency}>
                        {props.dataItem.Name
                            ? props.dataItem.YTDSales?.toLocaleString('en-US', {
                                  style: 'currency',
                                  currency: 'USD',
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 0,
                              })
                            : ''}
                    </td>
                )}
            />
            <GridColumn
                title='% of Total'
                field='PercentOfTotal'
                headerClassName='dataPercent'
                cell={(props: any) => (
                    <td className={styles.dataPercent}>
                        {props.dataItem.Name
                            ? props.dataItem.PercentOfTotal?.toLocaleString('en-US', {
                                  style: 'percent',
                                  minimumFractionDigits: 0,
                                  maximumFractionDigits: 1,
                              })
                            : ''}
                    </td>
                )}
            />
            <GridColumn
                title='# of Customers (Target)'
                field='NumberOfCustomers'
                cell={(props: any) => (
                    <td className={`${styles.dataLabel} ${styles.alignLeft}`}>
                        {props.dataItem.Name
                            ? `${props.dataItem.NumberOfCustomers} (${props.dataItem.NumberOfTargetAccounts})`
                            : ''}
                    </td>
                )}
            />
            <GridColumn
                title='Technical Knowledge'
                cell={(props: any) => (
                    <td className={styles.alignLeft} data-testid='technicalKnowledgeDropDown'>
                        {GridDropDownList(
                            TECHNICAL_KNOWLEDGE_LEVELS,
                            props.dataItem.Name ? props.dataItem.TechnicalKnowledge : '',
                            (newValue) => {
                                // Update TechnicalKnowledge value for employee in state
                                setGridData(
                                    gridData.map((g) =>
                                        g.EmployeeNo === props.dataItem.EmployeeNo
                                            ? { ...g, TechnicalKnowledge: newValue }
                                            : g
                                    )
                                )
                            }
                        )}
                    </td>
                )}
            />
            <GridColumn
                title='Sales Knowledge'
                cell={(props: any) => (
                    <td className={styles.alignLeft} data-testid='salesKnowledgeDropDown'>
                        {GridDropDownList(
                            SALES_KNOWLEDGE_LEVELS,
                            props.dataItem.Name ? props.dataItem.SalesKnowledge : '',
                            (newValue) => {
                                // Update SalesKnowledge value for employee in state
                                setGridData(
                                    gridData.map((g) =>
                                        g.EmployeeNo === props.dataItem.EmployeeNo
                                            ? { ...g, SalesKnowledge: newValue }
                                            : g
                                    )
                                )
                            }
                        )}
                    </td>
                )}
            />
            <GridColumn
                title='Certification'
                headerClassName='center'
                cell={(props: any) => (
                    <td className={`${styles.alignLeft} ${styles.radioButtonsContainer}`}>
                        <label className={styles.radioLabel}>
                            <input
                                type='radio'
                                onFocus={(e: any) => e.target.select()}
                                name={`certification${props.dataItem.EmployeeNo}`}
                                checked={props.dataItem.Certification === true}
                                onClick={() => {
                                    // Update Certification value for employee in state
                                    setGridData(
                                        gridData.map((g) =>
                                            g.EmployeeNo === props.dataItem.EmployeeNo
                                                ? { ...g, Certification: true }
                                                : g
                                        )
                                    )
                                }}
                            />
                            Yes
                        </label>
                        <label className={styles.radioLabel}>
                            <input
                                type='radio'
                                onFocus={(e: any) => e.target.select()}
                                name={`certification${props.dataItem.EmployeeNo}`}
                                checked={props.dataItem.Certification === false}
                                onClick={() => {
                                    // Update Certification value for employee in state
                                    setGridData(
                                        gridData.map((g) =>
                                            g.EmployeeNo === props.dataItem.EmployeeNo
                                                ? { ...g, Certification: false }
                                                : g
                                        )
                                    )
                                }}
                            />
                            No
                        </label>
                    </td>
                )}
            />
        </Grid>
    )
}
